import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShoppingCart, Plus, Minus, MagnifyingGlass, ForkKnife } from "@phosphor-icons/react";

export default function PublicMenu() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState("takeaway");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [openCart, setOpenCart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  useEffect(() => {
    api.get("/menu", { params: { available_only: true } }).then((r) => setItems(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    if (user && user.role === "customer") {
      setName(user.name);
      setPhone(user.phone || "");
    }
  }, [user]);

  const filtered = useMemo(() => items.filter((it) => {
    if (activeCat !== "all" && it.category_id !== activeCat) return false;
    if (q && !it.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, activeCat, q]);

  const add = (it) => setCart((c) => {
    const i = c.findIndex((x) => x.item.id === it.id);
    if (i >= 0) { const cc = [...c]; cc[i] = { ...cc[i], qty: cc[i].qty + 1 }; return cc; }
    return [...c, { item: it, qty: 1 }];
  });
  const inc = (id) => setCart((c) => c.map((x) => x.item.id === id ? { ...x, qty: x.qty + 1 } : x));
  const dec = (id) => setCart((c) => {
    const i = c.findIndex((x) => x.item.id === id);
    if (i < 0) return c;
    if (c[i].qty <= 1) return c.filter((_, j) => j !== i);
    const cc = [...c]; cc[i] = { ...cc[i], qty: cc[i].qty - 1 }; return cc;
  });

  const subtotal = cart.reduce((s, c) => s + c.item.price * c.qty, 0);
  const tax = subtotal * 0.05;
  const total = subtotal + tax;

  const placeOrder = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (!name || !phone) return toast.error("Add your name & phone");
    setBusy(true);
    try {
      const payload = {
        type: orderType,
        table_id: null,
        customer_name: name,
        customer_phone: phone,
        items: cart.map((c) => ({ item_id: c.item.id, quantity: c.qty, addons: [] })),
        tax_rate: 0.05,
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order placed: ${data.order_number}`);
      setPlacedOrder(data);
      setCart([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="public-menu-page">
      {/* Sticky header */}
      <header className="glass sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="public-brand">
          <div className="w-10 h-10 rounded-lg bg-[var(--brand)] text-white grid place-items-center font-display font-black text-xl">R</div>
          <div>
            <div className="font-display font-extrabold text-lg leading-none">Resto</div>
            <div className="label-eyebrow text-[9px] mt-1">Order online</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {!user || user === false ? (
            <Button variant="outline" onClick={() => nav("/login")} data-testid="public-signin">Sign in</Button>
          ) : (
            <span className="text-xs font-semibold text-[var(--text-secondary)] hidden sm:block">Hi, {user.name}</span>
          )}
          <Sheet open={openCart} onOpenChange={setOpenCart}>
            <SheetTrigger asChild>
              <Button className="btn-primary relative" data-testid="public-cart-btn">
                <ShoppingCart size={18} weight="duotone"/>
                <span className="ml-2 hidden sm:inline">Cart</span>
                {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-white text-[var(--brand)] text-[10px] font-bold rounded-full px-1.5">{cart.length}</span>}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="public-cart-sheet">
              <SheetHeader><SheetTitle className="font-display">Your order</SheetTitle></SheetHeader>
              {placedOrder ? (
                <div className="flex-1 flex flex-col justify-center items-center text-center p-6">
                  <div className="w-16 h-16 rounded-full bg-[var(--brand-2)]/20 text-[var(--brand-2)] grid place-items-center mb-4">
                    <ForkKnife size={28} weight="duotone"/>
                  </div>
                  <h3 className="font-display font-extrabold text-2xl mb-2">Order placed!</h3>
                  <div className="font-mono text-sm">{placedOrder.order_number}</div>
                  <div className="font-display font-extrabold text-3xl text-[var(--brand)] mt-2">{fmtMoney(placedOrder.total)}</div>
                  <p className="text-sm text-[var(--text-secondary)] mt-4 max-w-xs">
                    Our kitchen has received your order. You&apos;ll be notified when it&apos;s ready.
                  </p>
                  <Button onClick={() => { setPlacedOrder(null); setOpenCart(false); }} className="btn-primary mt-6" data-testid="public-order-done">
                    Order more
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto py-4">
                    {cart.length === 0 && <div className="text-sm text-[var(--text-secondary)] text-center py-10">Empty cart</div>}
                    <div className="space-y-2">
                      {cart.map((c) => (
                        <div key={c.item.id} className="flex items-center gap-3 p-3 bg-[var(--bg-secondary)]/50 rounded-md">
                          <img src={c.item.image_url} alt={c.item.name} className="w-12 h-12 rounded object-cover"/>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{c.item.name}</div>
                            <div className="text-xs text-[var(--text-secondary)]">{fmtMoney(c.item.price)}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => dec(c.item.id)} className="w-7 h-7 rounded bg-white grid place-items-center"><Minus size={12}/></button>
                            <div className="w-6 text-center text-sm font-bold">{c.qty}</div>
                            <button onClick={() => inc(c.item.id)} className="w-7 h-7 rounded bg-white grid place-items-center"><Plus size={12}/></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {cart.length > 0 && (
                    <div className="border-t border-[#EAE7E0] pt-4 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        {["takeaway", "delivery"].map((t) => (
                          <button key={t} onClick={() => setOrderType(t)} className={`py-2 rounded-md text-xs font-bold capitalize ${orderType === t ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-secondary)]"}`} data-testid={`public-type-${t}`}>{t}</button>
                        ))}
                      </div>
                      <p className="text-[11px] text-[var(--text-secondary)]">
                        Dine-in is table-based: scan your table QR and ask staff to take the order.
                      </p>
                      <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="public-name"/>
                      <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="public-phone"/>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>Subtotal</span><span className="font-semibold">{fmtMoney(subtotal)}</span></div>
                        <div className="flex justify-between"><span>Tax</span><span>{fmtMoney(tax)}</span></div>
                        <div className="flex justify-between font-display font-extrabold text-lg pt-2 border-t border-[#EAE7E0]"><span>Total</span><span className="text-[var(--brand)]">{fmtMoney(total)}</span></div>
                      </div>
                      <Button onClick={placeOrder} disabled={busy} className="w-full btn-primary h-12" data-testid="public-place-order">
                        {busy ? "Placing…" : "Place order"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-4 sm:px-8 py-12 sm:py-20">
        <div className="max-w-5xl mx-auto">
          <div className="label-eyebrow mb-3">Welcome</div>
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter mb-3">Crafted plates, ordered fast.</h1>
          <p className="text-base sm:text-lg text-[var(--text-secondary)] max-w-2xl">
            Browse our seasonal menu, build your order, and we&apos;ll have it ready in minutes. Dine-in, takeaway, or delivery — your choice.
          </p>
        </div>
      </section>

      {/* Search + categories */}
      <div className="px-4 sm:px-8 sticky top-[72px] bg-[var(--bg-primary)] z-20 py-3 border-y border-[#EAE7E0]">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes…" className="pl-9 bg-white" data-testid="public-search"/>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setActiveCat("all")} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${activeCat === "all" ? "bg-[var(--brand)] text-white" : "bg-white border border-[#EAE7E0]"}`} data-testid="public-cat-all">All</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setActiveCat(c.id)} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${activeCat === c.id ? "bg-[var(--brand)] text-white" : "bg-white border border-[#EAE7E0]"}`} data-testid={`public-cat-${c.name.toLowerCase()}`}>{c.name}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Items grid */}
      <main className="px-4 sm:px-8 py-6 sm:py-10 pb-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="public-menu-grid">
          {filtered.map((it) => (
            <div key={it.id} className="tile overflow-hidden flex flex-col" data-testid={`public-item-${it.id}`}>
              <div className="h-44 bg-[var(--bg-secondary)] overflow-hidden">
                {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-full object-cover"/>}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-display font-bold text-lg">{it.name}</div>
                <div className="text-xs text-[var(--text-secondary)] line-clamp-2 mt-1">{it.description}</div>
                <div className="flex items-center justify-between mt-3">
                  <div className="font-display font-extrabold text-xl text-[var(--brand)]">{fmtMoney(it.price)}</div>
                  <Button onClick={() => add(it)} className="btn-primary h-9" data-testid={`public-add-${it.id}`}>
                    <Plus size={14} weight="bold"/> Add
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-sm text-[var(--text-secondary)] text-center py-12">No items match your search.</div>}
        </div>
      </main>
    </div>
  );
}
